import * as THREE from 'three';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { getPhotorealisticTilesGroup } from './photorealisticTilesRegistry';

/** Docs: docs/SPECKLE_LIVE_SYNC.md — hyper-local clip + merge (no remesh; internal / ToS risk). */

export const DEFAULT_MAX_RADIUS_M = 40;

/**
 * Hyper-local capture of loaded Photorealistic tile meshes (no SimplifyModifier).
 * @returns {{ geometry: THREE.BufferGeometry, triangleCount: number, radiusM: number } | null}
 */
export function capturePhotorealisticLocal({
  center = [0, 0, 0],
  groundMeters = 80,
  maxRadiusM = DEFAULT_MAX_RADIUS_M,
  surfaceY = 0,
  verticalHalf = 40,
} = {}) {
  const group = getPhotorealisticTilesGroup();
  if (!group) {
    throw new Error('Photorealistic tiles are not loaded yet.');
  }

  const radiusM = Math.min(Math.max(groundMeters / 2, 8), maxRadiusM);
  const [cx, , cz] = center;
  const box = new THREE.Box3(
    new THREE.Vector3(cx - radiusM, surfaceY - verticalHalf, cz - radiusM),
    new THREE.Vector3(cx + radiusM, surfaceY + verticalHalf, cz + radiusM)
  );
  // Wide vertical band — Photorealistic tiles often sit away from planner Y=0
  const looseBox = new THREE.Box3(
    new THREE.Vector3(cx - radiusM, -5000, cz - radiusM),
    new THREE.Vector3(cx + radiusM, 5000, cz + radiusM)
  );

  const pieces = [];
  const scratchBox = new THREE.Box3();
  const scratchMat = new THREE.Matrix4();
  const centerXZ = new THREE.Vector3();
  const MAX_SOURCE_TRIS = 400000;
  let sourceTris = 0;
  let meshCount = 0;

  group.updateWorldMatrix(true, true);

  const collect = (useBox, xzOnly) => {
    group.traverse((obj) => {
      if (!obj.isMesh || !obj.geometry) return;
      if (obj.userData?.lotUi) return;
      if (sourceTris >= MAX_SOURCE_TRIS) return;
      meshCount += 1;
      scratchBox.setFromObject(obj);
      if (xzOnly) {
        scratchBox.getCenter(centerXZ);
        const dx = centerXZ.x - cx;
        const dz = centerXZ.z - cz;
        if (dx * dx + dz * dz > radiusM * radiusM) return;
      } else if (!scratchBox.intersectsBox(useBox)) {
        return;
      }

      const geom = obj.geometry.clone();
      if (!geom.getAttribute('position')) return;
      for (const key of Object.keys(geom.attributes)) {
        if (key !== 'position' && key !== 'normal') geom.deleteAttribute(key);
      }
      if (geom.index) geom.toNonIndexed();
      const tris = geom.getAttribute('position').count / 3;
      if (sourceTris + tris > MAX_SOURCE_TRIS && pieces.length) return;
      sourceTris += tris;
      scratchMat.copy(obj.matrixWorld);
      geom.applyMatrix4(scratchMat);
      pieces.push(geom);
    });
  };

  collect(box, false);
  if (!pieces.length) collect(looseBox, false);
  if (!pieces.length) collect(looseBox, true);

  if (!pieces.length) {
    throw new Error(
      meshCount === 0
        ? 'Photorealistic tiles have no meshes yet. Wait for LODs, Localize, then Recapture.'
        : `No tile meshes in hyper-local radius (${radiusM.toFixed(0)} m). Localize / zoom in, then Recapture.`
    );
  }

  const merged = BufferGeometryUtils.mergeGeometries(pieces, false);
  pieces.forEach((g) => g.dispose());
  if (!merged) {
    throw new Error('Failed to merge tile geometries.');
  }

  merged.computeVertexNormals();

  const pos = merged.getAttribute('position');
  const triangleCount = merged.index
    ? merged.index.count / 3
    : pos.count / 3;

  return { geometry: merged, triangleCount, radiusM };
}

/**
 * Three.js Y-up → Speckle Z-up (right-handed): (x, y, z) → (x, −z, y).
 * Speckle’s web viewer treats +Z as up; without this, ground planes stand on edge.
 */
export function threeToSpeckleZUp(x, y, z) {
  return [x, -z, y];
}

/** Flat vertex array Three Y-up → Speckle Z-up. */
export function threeVerticesToSpeckleZUp(vertices) {
  const out = new Array(vertices.length);
  for (let i = 0; i < vertices.length; i += 3) {
    const x = vertices[i];
    const y = vertices[i + 1];
    const z = vertices[i + 2];
    out[i] = x;
    out[i + 1] = -z;
    out[i + 2] = y;
  }
  return out;
}

/**
 * Convert BufferGeometry → Speckle mesh vertex/face arrays (Z-up meters).
 * Input geometry is Three.js Y-up world/local space.
 */
export function geometryToSpeckleArrays(geometry) {
  const pos = geometry.getAttribute('position');
  if (!pos) return { vertices: [], faces: [] };

  const verticesYup = [];
  for (let i = 0; i < pos.count; i += 1) {
    verticesYup.push(pos.getX(i), pos.getY(i), pos.getZ(i));
  }
  const vertices = threeVerticesToSpeckleZUp(verticesYup);

  const faces = [];
  if (geometry.index) {
    const idx = geometry.index;
    for (let i = 0; i < idx.count; i += 3) {
      faces.push(3, idx.getX(i), idx.getX(i + 1), idx.getX(i + 2));
    }
  } else {
    for (let i = 0; i < pos.count; i += 3) {
      faces.push(3, i, i + 1, i + 2);
    }
  }
  return { vertices, faces };
}
